<script lang="ts">
  // Schwebender Indikator (oben rechts, neben dem Terminal-Indikator):
  // sichtbar SOLANGE ein Auto-Rekey-Fenster offen ist. Zeigt die Restzeit
  // und beendet das Fenster per Klick. Bewusst auffaellig (warning-Farbe):
  // in dem Zeitfenster akzeptiert der Server einen geaenderten Host-Key,
  // das soll man sehen und schnell abbrechen koennen ("Schindluder").

  import { onMount } from "svelte";

  import { t } from "./i18n.svelte";
  import { Scharf } from "./scharf.svelte";
  import {
    closeRekeyWindow,
    getRekeyWindows,
    startRekeyWindows,
    type RekeyWindow,
  } from "./rekey-window.svelte";
  import { toast } from "./toast.svelte";

  const scharf = new Scharf();

  onMount(() => startRekeyWindows());

  let now = $state(Date.now());
  $effect(() => {
    const id = setInterval(() => (now = Date.now()), 1_000);
    return () => clearInterval(id);
  });

  const wins = $derived<RekeyWindow[]>(getRekeyWindows());
  // Das am naechsten ablaufende Fenster treibt die Restzeit-Anzeige.
  const soonest = $derived<RekeyWindow | null>(
    wins.length === 0
      ? null
      : [...wins].sort(
          (a, b) => Date.parse(a.expires_at) - Date.parse(b.expires_at),
        )[0],
  );

  const remaining = $derived(
    soonest ? Math.max(0, Date.parse(soonest.expires_at) - now) : 0,
  );

  function fmt(ms: number): string {
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    return `${m}:${String(s % 60).padStart(2, "0")}`;
  }

  let closing = $state(false);
  async function endWindow(): Promise<void> {
    if (!soonest || closing) return;
    // Zweite Geste am Knopf selbst (Scharf schalten): der erste Klick
    // fragt, der zweite beendet. Vorher fragte der Browser.
    if (!scharf.klick("rekey-ende")) return;
    closing = true;
    try {
      await closeRekeyWindow(soonest.host);
      toast.info(t("rekey.indicator.ended", "Auto-Rekey beendet"));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      closing = false;
    }
  }
</script>

{#if soonest}
  <button
    type="button"
    onclick={endWindow}
    disabled={closing}
    title={scharf.ist("rekey-ende")
      ? t("rekey.indicator.frage_title", `Auto-Rekey für ${soonest.host} wirklich beenden? Nochmal klicken`)
      : t("rekey.indicator.title", `Auto-Rekey aktiv für ${soonest.host} — Klick zum Beenden`)}
    aria-label={t("rekey.indicator.aria", "Auto-Rekey aktiv — beenden")}
    class="{scharf.ist('rekey-ende') ? 'bg-danger/15 text-danger hover:bg-danger/25' : 'bg-warning/15 text-warning hover:bg-warning/25'} pointer-events-auto relative flex h-7 items-center gap-1.5 rounded-full px-2.5 text-xs font-medium tabular-nums transition disabled:opacity-50"
  >
    <!-- Schluessel-Icon -->
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <circle cx="7.5" cy="15.5" r="4.5" />
      <path d="M10.5 12.5 21 2" />
      <path d="M16 7l3 3" />
    </svg>
    <span>{scharf.ist("rekey-ende") ? t("rekey.indicator.frage", "beenden? nochmal klicken") : fmt(remaining)}</span>
    {#if wins.length > 1}
      <span class="bg-warning text-bg absolute -right-1 -top-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full px-1 text-[9px] font-bold leading-none">
        {wins.length}
      </span>
    {/if}
  </button>
{/if}
